import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Reputation } from '@sorare/core/src/atoms/icons/rivals/branded/Reputation';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Avatar } from '@sorare/core/src/components/user/Avatar';

import { Row_footballRivalsDivisionRanking } from './__generated__/index.graphql';

const Root = styled.div`
  height: calc(6 * var(--unit));
  display: grid;
  grid-template-columns: 50px min-content 1fr min-content 50px;
  column-gap: var(--unit);
  align-items: center;

  &.promotion {
    border-left: var(--unit) solid var(--c-rivals-friendlies);
  }
  &.relegation {
    border-left: var(--unit) solid var(--c-rivals-loss);
  }
  &.highlight {
    background-color: var(--c-nd-100);
  }
`;
const RightCentered = styled(Horizontal)`
  justify-content: flex-end;
  padding-right: var(--unit);
`;
const Reward = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
`;

type Props = {
  ranking: Row_footballRivalsDivisionRanking;
  highlight: boolean;
};
export const Row = ({ ranking, highlight }: Props) => {
  const probabilisticBundleConfiguration =
    ranking.eligibleReward?.probabilisticBundleConfiguration;
  const promotion = ranking.divisionChange === DivisionChange.PROMOTED;
  const relegation = ranking.divisionChange === DivisionChange.RELEGATED;

  return (
    <Root
      className={classNames({
        promotion,
        relegation,
        highlight,
      })}
    >
      <LabelL bold className="text-center">
        {ranking.ranking}
      </LabelL>
      <Avatar user={ranking.manager.user} size={3} rounded />
      <LabelL bold>
        <Ellipsis>{ranking.manager.user.nickname}</Ellipsis>
      </LabelL>
      <div>
        {probabilisticBundleConfiguration && (
          <Reward src={probabilisticBundleConfiguration.pictureUrl} alt="" />
        )}
      </div>
      <RightCentered gap={0}>
        <LabelL bold>{ranking.points}</LabelL>
        <Reputation height={12} hideCount />
      </RightCentered>
    </Root>
  );
};

Row.fragments = {
  footballRivalsDivisionRanking: gql`
    fragment Row_footballRivalsDivisionRanking on FootballRivalsDivisionRanking {
      id
      ranking
      points
      divisionChange
      manager {
        id
        user {
          slug
          nickname
          ...Avatar_user
        }
      }
      eligibleReward {
        probabilisticBundleConfiguration {
          id
          pictureUrl
        }
      }
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<Row_footballRivalsDivisionRanking>,
};
